import { useEffect, useMemo, useRef, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { format, startOfDay } from 'date-fns'

type EventDateTimePickerProps = {
  label: string
  value: string | null
  onChange: (nextIso: string | null) => void
  min?: string | null
  required?: boolean
  placeholder?: string
}

function toLocalDateAndTime(iso: string | null | undefined): { date: Date | undefined; time: string } {
  if (!iso) return { date: undefined, time: '18:00' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { date: undefined, time: '18:00' }
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

function localDateAndTimeToUtcIso(date: Date | undefined, time: string): string | null {
  if (!date || !time) return null
  const [hh, mm] = String(time || '').split(':').map((v) => Number(v))
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm, 0, 0)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

function formatDisplay(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return format(d, 'EEE, MMM d · h:mm a')
}

function utcIsoToMs(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

function nextHalfHourTime(): string {
  const d = new Date()
  d.setSeconds(0, 0)
  const minutes = d.getMinutes()
  const rounded = minutes <= 30 ? 30 : 60
  if (rounded === 60) d.setHours(d.getHours() + 1, 0, 0, 0)
  else d.setMinutes(30, 0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function EventDateTimePicker({
  label,
  value,
  onChange,
  min = null,
  required = false,
  placeholder,
}: EventDateTimePickerProps) {
  const [open, setOpen] = useState(false)
  const [draftDate, setDraftDate] = useState<Date | undefined>(undefined)
  const [timeValue, setTimeValue] = useState('18:00')
  const [month, setMonth] = useState<Date>(new Date())
  const rootRef = useRef<HTMLDivElement | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const parts = toLocalDateAndTime(value)
    setDraftDate(parts.date)
    setTimeValue(parts.time || nextHalfHourTime())
    if (parts.date) setMonth(parts.date)
  }, [value])

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const root = rootRef.current
      if (!root) return
      if (!root.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const display = useMemo(() => formatDisplay(value), [value])
  const minDate = useMemo(() => {
    if (!min) return undefined
    const d = new Date(min)
    if (Number.isNaN(d.getTime())) return undefined
    return d
  }, [min])
  const minDay = useMemo(() => (minDate ? startOfDay(minDate) : undefined), [minDate])
  const minMs = utcIsoToMs(min)
  const candidateIso = localDateAndTimeToUtcIso(draftDate, timeValue)
  const candidateMs = utcIsoToMs(candidateIso)
  const belowMin = minMs != null && candidateMs != null && candidateMs < minMs
  const setDisabled = !candidateIso || belowMin

  return (
    <div className="relative" ref={rootRef}>
      <label className="text-xs text-white/65">
        {label}
        {required ? ' (required)' : ''}
      </label>
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="mt-1 h-7 w-full rounded-md border border-white/10 bg-black px-2 text-left text-sm text-white/90 outline-none focus:border-white/20"
      >
        {display || placeholder || `Select ${label.toLowerCase()}...`}
      </button>
      {open ? (
        <div className="absolute z-40 mt-2 w-[300px] rounded-xl border border-white/10 bg-neutral-900 p-3 shadow-2xl">
          <div className="space-y-2">
            <div data-testid="calendar">
              <DayPicker
                mode="single"
                month={month}
                onMonthChange={setMonth}
                selected={draftDate}
                onSelect={setDraftDate}
                disabled={minDay ? { before: minDay } : undefined}
                className="text-white"
                classNames={{
                  caption: 'flex items-center justify-between px-1',
                  caption_label: 'text-sm font-semibold',
                  nav: 'flex items-center gap-1',
                  nav_button: 'h-7 w-7 rounded-md border border-white/10 bg-black text-white/85 hover:bg-white/10',
                  table: 'w-full border-collapse',
                  head_row: 'grid grid-cols-7 mb-1',
                  head_cell: 'text-[11px] text-white/55 text-center',
                  row: 'grid grid-cols-7',
                  cell: 'p-0 text-center',
                  day: 'h-8 w-8 rounded-md text-sm hover:bg-white/10',
                  day_selected: 'bg-brand text-white hover:bg-brand/90',
                  day_today: 'border border-white/30',
                  day_disabled: 'opacity-35',
                }}
              />
            </div>
            <input
              data-testid="time-input"
              type="time"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              step={60}
              className="h-8 w-full rounded-md border border-white/10 bg-black px-2 text-sm text-white outline-none focus:border-white/20"
            />
            {belowMin ? <div className="text-xs text-red-300">End cannot be before start.</div> : null}
            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => {
                  onChange(null)
                  setOpen(false)
                  btnRef.current?.focus()
                }}
                className="h-7 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white/85 hover:bg-white/10"
              >
                Clear
              </button>
              <button
                type="button"
                disabled={setDisabled}
                onClick={() => {
                  if (!candidateIso || belowMin) return
                  onChange(candidateIso)
                  setOpen(false)
                  btnRef.current?.focus()
                }}
                className="h-7 rounded-md border border-white/15 bg-white/5 px-3 text-xs text-white/90 hover:bg-white/10 disabled:opacity-50"
              >
                Set
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export { formatDisplay as formatEventDateTimeDisplay, localDateAndTimeToUtcIso }
